import { useState, useCallback } from 'react'
import { aiPdfParsingService } from '../services/aiPdfParsingService'
import { AiParsingStatus, AiParsingResult, ParsedApplicationData } from '../types'

export const useAiPdfParsing = () => {
  const [status, setStatus] = useState<AiParsingStatus>({
    isLoading: false,
    isSuccess: false,
    isError: false,
    progress: 0
  })

  const [parsedData, setParsedData] = useState<ParsedApplicationData | null>(null)

  const parseFile = useCallback(async (file: File): Promise<AiParsingResult> => {
    // Reset state
    setStatus({
      isLoading: true,
      isSuccess: false,
      isError: false,
      progress: 0,
      estimatedTimeRemaining: aiPdfParsingService.getEstimatedProcessingTime(file.size)
    })
    setParsedData(null)

    try {
      // Start progress simulation
      const stopProgress = aiPdfParsingService.createProgressSimulator(
        (progress, estimatedTimeMs) => {
          setStatus(prev => ({
            ...prev,
            progress,
            estimatedTimeRemaining: estimatedTimeMs
          }))
        },
        aiPdfParsingService.getEstimatedProcessingTime(file.size)
      )

      // Parse the file
      const result = await aiPdfParsingService.parseApplication(file)

      // Stop progress simulation
      stopProgress()

      if (result.success && result.data) {
        setStatus({
          isLoading: false,
          isSuccess: true,
          isError: false,
          progress: 100
        })
        setParsedData(result.data)
      } else {
        setStatus({
          isLoading: false,
          isSuccess: false,
          isError: true,
          error: result.error || 'Parsing failed',
          progress: 0
        })
      }

      return result
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      
      setStatus({
        isLoading: false,
        isSuccess: false,
        isError: true,
        error: errorMessage,
        progress: 0
      })

      return {
        success: false,
        error: errorMessage,
        processingTimeMs: 0
      }
    }
  }, [])

  const parseMultipleFiles = useCallback(async (files: File[]): Promise<AiParsingResult> => {
    console.log('🔍 parseMultipleFiles called with:', files.map(f => f.name))
    
    // Reset state
    const totalSize = files.reduce((sum, file) => sum + file.size, 0)
    setStatus({
      isLoading: true,
      isSuccess: false,
      isError: false,
      progress: 0,
      estimatedTimeRemaining: aiPdfParsingService.getEstimatedProcessingTime(totalSize) * 1.5 // Multiple files take longer
    })
    setParsedData(null)

    try {
      // Start progress simulation (longer for multiple files)
      const stopProgress = aiPdfParsingService.createProgressSimulator(
        (progress, estimatedTimeMs) => {
          setStatus(prev => ({
            ...prev,
            progress,
            estimatedTimeRemaining: estimatedTimeMs
          }))
        },
        aiPdfParsingService.getEstimatedProcessingTime(totalSize) * 1.5
      )

      // Parse multiple files
      const result = await aiPdfParsingService.parseMultipleApplications(files)

      // Stop progress simulation
      stopProgress()

      if (result.success && result.data) {
        setStatus({
          isLoading: false,
          isSuccess: true,
          isError: false,
          progress: 100
        })
        setParsedData(result.data)
      } else {
        setStatus({
          isLoading: false,
          isSuccess: false,
          isError: true,
          error: result.error || 'Parsing failed',
          progress: 0
        })
      }

      return result
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      
      setStatus({
        isLoading: false,
        isSuccess: false,
        isError: true,
        error: errorMessage,
        progress: 0
      })

      return {
        success: false,
        error: errorMessage,
        processingTimeMs: 0
      }
    }
  }, [])

  const reset = useCallback(() => {
    setStatus({
      isLoading: false,
      isSuccess: false,
      isError: false,
      progress: 0
    })
    setParsedData(null)
  }, [])

  return {
    parseFile,
    parseMultipleFiles,
    reset,
    status,
    parsedData,
    // Convenience flags
    isLoading: status.isLoading,
    isSuccess: status.isSuccess,
    isError: status.isError,
    error: status.error,
    progress: status.progress,
    estimatedTimeRemaining: status.estimatedTimeRemaining
  }
}
